import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// Import custom components
import SimpleAccordion from '../../ui_reusable/SimpleAccordion'
import { SectionTitle } from '../../ui_reusable/styles/SectionTitle.styles'
import { TagFilledActive, TagFilledInactive } from '../../ui_reusable/Tags'
import { ThickSelectPicker } from '../../ui_reusable/StandardSelectPickers'
import { HeaderContainer } from '../../ui_reusable/styles/PlacementMisc.styles'
// Import existing components
import ClientOverview from './client/ClientOverview'
import ClientDeactivate from './client/ClientDeactivate'
import ClientUserList from './client/ClientUserList'
import ClientProjectList from './client/ClientProjectList'
import CreateUser from './client/CreateUser'
import ClientInternalDriverList from './client/ClientInternalDriverList'
import ClientExternalDriverList from './client/ClientExternalDriverList'
import { useClientContext } from '../../../context/ClientContext'
import { clientPickerLabel } from '../../../context/ClientProjectContext'

const ClientPage = () => {
  const navigate = useNavigate()
  const { client: clientParam } = useParams()
  const id = clientParam
  const [client, setClient] = useState(null)
  const [clientOptions, setClientOptions] = useState([])
  const [refresh, setRefresh] = useState(new Date().getTime())
  const { setSelectedClient } = useClientContext()

  useEffect(() => {
    const abortController = new AbortController()

    const getClient = async () => {
      if (!id) return
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/clients/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const res = await response.json()

        setClient(res.data)
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch client:', error)
        }
      }
    }

    getClient()

    return () => {
      abortController.abort()
    }
  }, [id, refresh])

  useEffect(() => {
    const abortController = new AbortController()

    const getClientOptions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/v2/clients/?ordering=name`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )
        const res = await response.json()

        setClientOptions(res.data || [])
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to fetch client options:', error)
          setClientOptions([])
        }
      }
    }

    getClientOptions()

    return () => {
      abortController.abort()
    }
  }, [refresh])

  const handleManage = (nextId) => {
    if (!nextId || String(nextId) === String(id)) return

    const nextClient = clientOptions.find((elem) => elem.id === nextId)
    if (nextClient) {
      setSelectedClient({
        id: nextClient.id,
        name: nextClient.name,
        logo: nextClient.logo,
      })
    }
    navigate('/clients/' + nextId)
  }

  if (!client) return null

  return (
    <>
      {/* Header section with title, status and client picker */}
      <HeaderContainer>
        <SectionTitle>Manage Client</SectionTitle>

        {client.is_active ? (
          <TagFilledActive label="Client is active" />
        ) : (
          <TagFilledInactive label="Client is inactive" />
        )}

        <ThickSelectPicker
          placeholder="Select client"
          value={client.id}
          size="lg"
          data={clientOptions.map((item, i) => ({
            key: i + 1,
            label: clientPickerLabel(
              item.name,
              item.id === client.id ? client.is_active : item.is_active,
            ),
            value: item.id,
          }))}
          onChange={handleManage}
          style={{ width: 250 }}
        />
      </HeaderContainer>

      {/* Main content sections using SimpleAccordion */}
      <SimpleAccordion
        id={'manageClientPageAccordion'}
        defaultActiveKey={['overview']}
      >
        <SimpleAccordion.Panel header={'Client settings'} eventKey="overview">
          <ClientOverview client={client} />
        </SimpleAccordion.Panel>

        <SimpleAccordion.Panel header={'Users'} eventKey="users">
          {client.is_active && (
            <CreateUser clientId={id} setRefresh={setRefresh} />
          )}
          <ClientUserList
            clientId={id}
            refresh={refresh}
            setRefresh={setRefresh}
          />
        </SimpleAccordion.Panel>

        <SimpleAccordion.Panel header={'Projects'} eventKey="projects">
          <ClientProjectList clientId={id} />
        </SimpleAccordion.Panel>

        <SimpleAccordion.Panel
          header={'Internal Drivers'}
          eventKey="internal_drivers"
        >
          <ClientInternalDriverList
            clientId={id}
            disabled={!client.is_active}
          />
        </SimpleAccordion.Panel>

        <SimpleAccordion.Panel
          header={'External Drivers'}
          eventKey="external_drivers"
        >
          <ClientExternalDriverList
            clientId={id}
            disabled={!client.is_active}
          />
        </SimpleAccordion.Panel>

        {/*
                <SimpleAccordion.Panel
                    header={'Change log'}
                    eventKey="changelog"

                >

                </SimpleAccordion.Panel>
*/}

        <SimpleAccordion.Panel
          header={'Deactivate and delete'}
          eventKey="deactivate"
          color="#fffcf5"
        >
          <ClientDeactivate client={client} setRefresh={setRefresh} />
        </SimpleAccordion.Panel>
      </SimpleAccordion>
    </>
  )
}

export default ClientPage
